import { Stack } from "@altimateai/lego";
import { Card, CardContent, Typography, toast } from "@altimateai/lego";
import { cn } from "@/lib/utils";
import { IconInfoCircle } from "@tabler/icons-react";
import { useState } from "react";
import { useMutation } from '@tanstack/react-query';
import { SelectButton } from "../../../components/copyButton";
import { createDemoSession } from "../../../helpers/apis";
import { useAppState } from "../../../modules/app/useAppContext";
import { PathConfig, pathConfigs, PathOption } from "../constants";
import { DatamatesSection } from "./sections/DatamatesSection";
import { DbtSection } from "./sections/DbtSection";
import { PlaygroundSection } from "./sections/PlaygroundSection";

function PathSelector() {
  const { enablePromptLibrary } = useAppState();
  const [selectedPath, setSelectedPath] = useState<PathConfig>(
    () => pathConfigs[enablePromptLibrary ? PathOption.Playground : PathOption.Datamates]
  );

  const { mutate: launchDemo, isPending: isDemoLoading } = useMutation({
    mutationFn: createDemoSession,
    onSuccess: response => {
      if (!response) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to launch demo playground. Please try again.",
        });
        return;
      }
      const demoUrl = `${response.demoUrl}/demo?demoSessionId=${encodeURIComponent(response.sessionId)}`;
      window.open(demoUrl, "_blank", "noopener,noreferrer");
    },
    onError: error => {
      console.error("Demo session creation failed:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to launch demo playground. Please try again.",
      });
    },
  });

  const availablePaths = Object.values(pathConfigs).filter(
    config => config.option !== PathOption.Playground || enablePromptLibrary
  );

  const renderSection = () => {
    const sections = {
      [PathOption.Playground]: (
        <PlaygroundSection launchDemo={launchDemo} isDemoLoading={isDemoLoading} />
      ),
      [PathOption.Datamates]: <DatamatesSection />,
      [PathOption.Dbt]: <DbtSection />,
    };
    return sections[selectedPath.option];
  };

  return (
    <Stack direction="column" gap="xl">
      <Card className="al-w-full al-rounded-xl al-pt-8 al-pb-4 al-px-4 !al-bg-blue-50">
        <CardContent>
          <Stack direction="column" gap="2xl">
            <Stack direction="column" className="al-mb-2">
              <Typography variant="h3" weight="semibold" className="al-text-gray-900">
                Choose Your Path
              </Typography>
              <Typography size="sm" className="al-text-gray-600">
                Select the product you want to get started with
              </Typography>
            </Stack>

            <Stack direction="column" gap="sm" className="al-mb-1 sm:al-flex-row sm:al-flex-wrap">
              {availablePaths.map(config => (
                <SelectButton
                  key={config.option}
                  text={config.label}
                  textColor={config.textColor}
                  borderColor={config.borderColor}
                  bgColor={config.bgColor}
                  hoverBgColor={config.hoverBgColor}
                  selected={selectedPath.option === config.option}
                  onClick={() => setSelectedPath(config)}
                />
              ))}
            </Stack>

            <Stack
              direction="row"
              align="start"
              gap="sm"
              className={cn(
                selectedPath.cardBgColor,
                selectedPath.borderColor,
                "al-border-l-4 al-p-4 al-rounded-r-lg"
              )}
            >
              <IconInfoCircle
                size={20}
                className={cn("al-shrink-0", selectedPath.textColor)}
              />
              <Typography
                size="sm"
                className={selectedPath.textColor}
              >
                {selectedPath.description}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {renderSection()}
    </Stack>
  );
}

export default PathSelector;
